import React, {useEffect, useState} from "react";
import {Modal} from "react-bootstrap";
import {genericGetOne} from "../generic/crud";
import {ENTITY_ID_TREATMENT, ENTITY_ID_DEALER} from "../generic/Constants";

const SEGMENT_DISPLAY_NAMES = {
    'SMALL': 'Small',
    'MEDIUM': 'Medium',
    'LARGE': 'Large',
    'PREMIUM': 'Premium'
};

const JOB_TYPE_DISPLAY_NAMES = {
    'FREE_SERVICE': 'Free Service',
    'PAID_SERVICE': 'Paid Service',
    'RUNNING_REPAIR': 'Running Repair',
    'ACCIDENTAL_REPAIR': 'Accidental Repair'
};

export function TreatmentDetailsDialog(props) {
    const {show, handleClose, treatmentId, onSave, onEdit} = props;
    const [treatment, setTreatment] = useState(null);
    const [dealer, setDealer] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleEditClick = () => {
        // Close the details dialog
        handleClose();
        // Notify parent to open edit dialog
        if (onEdit) {
            onEdit(treatmentId);
        }
    };

    useEffect(() => {
        if (treatmentId && show) {
            setIsLoading(true);

            // Fetch treatment details
            genericGetOne(ENTITY_ID_TREATMENT, treatmentId).then(
                ({data}) => {
                    setTreatment(data);

                    // Fetch dealer details if dealerId exists
                    if (data.dealerId) {
                        genericGetOne(ENTITY_ID_DEALER, data.dealerId).then(
                            ({data: dealerData}) => {
                                setDealer(dealerData);
                            }
                        ).catch(() => {
                            setDealer(null);
                        });
                    }

                    setIsLoading(false);
                })
                .catch(() => {
                    setIsLoading(false);
                });
        }
    }, [treatmentId, show]);

    if (!treatment) {
        return null;
    }

    const renderPricingTable = () => {
        if (!treatment.pricing || treatment.pricing.length === 0) {
            return <p className="text-muted">No pricing information available</p>;
        }

        const isPricingType = treatment.pricingType === 'SEGMENT_PRICING';
        const displayNames = isPricingType ? SEGMENT_DISPLAY_NAMES : JOB_TYPE_DISPLAY_NAMES;

        return (
            <div className="table-responsive">
                <table className="table table-row-bordered table-hover">
                    <thead>
                        <tr className="fw-bold fs-6 text-gray-800 border-bottom-2 border-gray-200">
                            <th>{isPricingType ? 'Segment' : 'Job Type'}</th>
                            <th>Dealer Price</th>
                            <th>Customer Price</th>
                        </tr>
                    </thead>
                    <tbody>
                        {treatment.pricing.map((price, index) => {
                            const typeKey = isPricingType ? price.segment : price.jobType;
                            const displayName = displayNames[typeKey] || typeKey;

                            return (
                                <tr key={index}>
                                    <td className="fw-bold text-gray-800">{displayName}</td>
                                    <td className="text-success fw-bold">₹{price.dealerPrice?.toFixed(2) || '0.00'}</td>
                                    <td className="text-danger fw-bold">₹{price.customerPrice?.toFixed(2) || '0.00'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            aria-labelledby="treatment-details-modal">
            <Modal.Header closeButton>
                <Modal.Title>Treatment Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoading && (
                    <div className="text-center py-10">
                        <span className="spinner-border spinner-border-lg"></span>
                        <p className="mt-3">Loading treatment information...</p>
                    </div>
                )}

                {!isLoading && treatment && (
                    <>
                        {/* Treatment Name & Basic Info */}
                        <div className="row mb-5">
                            <div className="col-12 mb-5">
                                <div className="card h-100 bg-light-primary border border-primary">
                                    <div className="card-body p-6">
                                        <div className="d-flex align-items-start justify-content-between">
                                            <div className="flex-grow-1">
                                                <label className="fw-bold text-primary fs-7 mb-1">Treatment Name</label>
                                                <h3 className="fw-bolder text-gray-900 mb-0">{treatment.name}</h3>
                                                {treatment.description && (
                                                    <div className="text-muted fs-7 mt-1">{treatment.description}</div>
                                                )}
                                                <div className="mt-4 d-flex gap-2">
                                                    <span className={`badge ${treatment.category === 'HVT' ? 'badge-primary' : 'badge-info'} fs-7`}>
                                                        {treatment.category === 'HVT' ? 'High Value Treatment' : 'Low Value Treatment'}
                                                    </span>
                                                    {treatment.isVAS ? (
                                                        <span className="badge badge-success fs-7">VAS</span>
                                                    ) : (
                                                        <span className="badge badge-danger fs-7">NON VAS</span>
                                                    )}
                                                </div>
                                            </div>
                                            {/* Usage Statistics Badge */}
                                            <div className="text-end ms-4">
                                                {(() => {
                                                    const count = treatment.usageCount || 0;
                                                    let iconClass = 'bi-star';
                                                    let iconColor = 'text-secondary';
                                                    let popularityText = 'New';
                                                    let badgeColor = 'badge-light-secondary';

                                                    if (count >= 50) {
                                                        iconClass = 'bi-fire';
                                                        iconColor = 'text-danger';
                                                        popularityText = 'Hot';
                                                        badgeColor = 'badge-danger';
                                                    } else if (count >= 20) {
                                                        iconClass = 'bi-star-fill';
                                                        iconColor = 'text-warning';
                                                        popularityText = 'Popular';
                                                        badgeColor = 'badge-warning';
                                                    } else if (count >= 5) {
                                                        iconClass = 'bi-star-half';
                                                        iconColor = 'text-primary';
                                                        popularityText = 'Trending';
                                                        badgeColor = 'badge-primary';
                                                    } else if (count > 0) {
                                                        iconClass = 'bi-star';
                                                        iconColor = 'text-info';
                                                        popularityText = 'Used';
                                                        badgeColor = 'badge-info';
                                                    }

                                                    return (
                                                        <div className="d-flex flex-column align-items-end gap-2">
                                                            <span className={`badge ${badgeColor} fs-7`}>
                                                                <i className={`bi ${iconClass} me-1`}></i>
                                                                {popularityText}
                                                            </span>
                                                            <span className="text-gray-700 fw-bold fs-6">
                                                                <i className="bi bi-graph-up me-1"></i>
                                                                {count} {count === 1 ? 'use' : 'uses'}
                                                            </span>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* HSN Number & Dealer */}
                        <div className="row mb-5">
                            {treatment.hsnNumber && (
                                <div className="col-md-6 mb-4">
                                    <div className="card h-100 border border-gray-300">
                                        <div className="card-body p-5">
                                            <div className="d-flex align-items-center">
                                                <i className="bi bi-upc-scan text-primary fs-2 me-3"></i>
                                                <div>
                                                    <label className="fw-bold text-gray-600 fs-7 mb-1">HSN/SAC Number</label>
                                                    <h5 className="fw-bolder text-gray-900 mb-0">{treatment.hsnNumber}</h5>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {dealer && (
                                <div className="col-md-6 mb-4">
                                    <div className="card h-100 border border-gray-300">
                                        <div className="card-body p-5">
                                            <div className="d-flex align-items-center">
                                                <i className="bi bi-shop text-info fs-2 me-3"></i>
                                                <div>
                                                    <label className="fw-bold text-gray-600 fs-7 mb-1">Dealer</label>
                                                    <h5 className="fw-bolder text-gray-900 mb-1">{dealer.name}</h5>
                                                    {dealer.brand && (
                                                        <span className="badge badge-light-primary">
                                                            <i className="bi bi-tag-fill me-1"></i>
                                                            {dealer.brand}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Pricing Type & Table */}
                        <div className="separator separator-dashed my-5"></div>

                        <h5 className="fw-bolder text-gray-900 mb-4">
                            <i className="bi bi-currency-rupee fs-4 text-primary me-2"></i>
                            Pricing Information
                            <span className="badge badge-light-success ms-3">
                                {treatment.pricingType === 'SEGMENT_PRICING' ? 'Segment-Based Pricing' : 'Job Type Pricing'}
                            </span>
                        </h5>

                        <div className="card border border-gray-300 mb-5">
                            <div className="card-body p-5">
                                {renderPricingTable()}
                            </div>
                        </div>

                        {/* Additional Information */}
                        <div className="separator separator-dashed my-4"></div>

                        <div className="row">
                            <div className="col-md-6">
                                <div className="d-flex align-items-center mb-3">
                                    <i className="bi bi-calendar-plus text-gray-600 fs-5 me-3"></i>
                                    <div>
                                        <label className="fw-bold text-gray-600 fs-8 mb-0">Created On</label>
                                        <p className="text-gray-800 fs-7 mb-0">
                                            {treatment.createdOn ? new Date(treatment.createdOn).toLocaleString() : 'N/A'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex align-items-center mb-3">
                                    <i className="bi bi-calendar-check text-gray-600 fs-5 me-3"></i>
                                    <div>
                                        <label className="fw-bold text-gray-600 fs-8 mb-0">Last Updated</label>
                                        <p className="text-gray-800 fs-7 mb-0">
                                            {treatment.updatedOn ? new Date(treatment.updatedOn).toLocaleString() : 'N/A'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleEditClick}>
                    <i className="bi bi-pencil-square me-2"></i>
                    Edit
                </button>
                <button
                    type="button"
                    className="btn btn-light"
                    onClick={handleClose}>
                    Close
                </button>
            </Modal.Footer>
        </Modal>
    );
}
